/**
 * @file task-mutations.ts
 * @description Выполнение действий с задачей и разбор отказов сервера
 * (phase19.31, crm.md T3–T4; контракт §13).
 *
 * Отказы, после которых данные на экране устарели (задачу одновременно
 * изменили, её уже выполнили, исполнителя отключили), показываются
 * человеческим текстом, и задача со списком перечитываются — человек сразу
 * видит актуальное состояние, а не жмёт кнопку повторно вслепую.
 * Тексты — реестром по коду ошибки.
 *
 * Флажок «выполнено» в списках — `completeTaskWithUndo`: уведомление с
 * «Отменить», которое возвращает задачу в работу (`POST …/reopen`).
 */

import { ApiError } from "../../../lib/api-client";
import { completeActivity, reopenActivity } from "../../../lib/api-crm";
import type { NotifyAction } from "../../ui/NotifyProvider";

/** Отказы, после которых перечитываем данные, и их текст. */
export const TASK_STALE_ERROR_TEXT: Record<string, string> = {
  activity_changed_concurrently: "Задачу только что изменил другой сотрудник — показываем актуальную версию",
  activity_completed: "Задачу уже отметили выполненной — показываем актуальную версию",
  activity_not_found: "Задачу удалили — список обновлён",
  activity_complete_forbidden: "Отметить выполненной может исполнитель, автор задачи или владелец магазина",
  activity_reopen_forbidden: "Вернуть в работу может исполнитель, автор задачи или владелец магазина",
  activity_assignee_invalid: "Этого сотрудника нельзя назначить: у него нет доступа к клиентам и сделкам. Список обновлён",
};

/** Тексты действий «выполнено» / «вернуть в работу». */
export const TASK_ACTION_TEXT = {
  completed: "Задача выполнена",
  completeFailed: "Не удалось отметить задачу выполненной",
  undo: "Отменить",
  reopened: "Задача возвращена в работу",
  reopenFailed: "Не удалось вернуть задачу в работу",
} as const;

export interface TaskMutationDeps {
  notify: {
    success: (message: string, options?: { action?: NotifyAction }) => void;
    error: (message: string) => void;
  };
  /** Перечитать задачу и список. */
  reload: () => void;
}

export interface TaskMutationTexts {
  success: string;
  /** Текст, если сервер не объяснил причину (нет связи и т. п.). */
  fallback: string;
  /** Кнопка в уведомлении об успехе — например, «Отменить». */
  action?: NotifyAction;
}

/**
 * Выполнить действие: успех — уведомление и перечитывание; устаревшие
 * данные — текст из реестра и перечитывание; прочее — текст сервера.
 * Возвращает результат или `null` при отказе.
 */
export async function runTaskMutation<T>(
  action: () => Promise<T>,
  texts: TaskMutationTexts,
  deps: TaskMutationDeps,
): Promise<T | null> {
  try {
    const result = await action();
    if (texts.action) deps.notify.success(texts.success, { action: texts.action });
    else deps.notify.success(texts.success);
    deps.reload();
    return result;
  } catch (err) {
    const stale = err instanceof ApiError ? TASK_STALE_ERROR_TEXT[err.code] : undefined;
    if (stale) {
      deps.notify.error(stale);
      deps.reload();
      return null;
    }
    deps.notify.error(err instanceof ApiError && err.message ? err.message : texts.fallback);
    return null;
  }
}

/** Вернуть задачу в работу (кнопка окна задачи и «Отменить» в уведомлении). */
export function reopenTask(taskId: string, deps: TaskMutationDeps) {
  return runTaskMutation(
    () => reopenActivity(taskId),
    { success: TASK_ACTION_TEXT.reopened, fallback: TASK_ACTION_TEXT.reopenFailed },
    deps,
  );
}

/** Флажок «выполнено»: отметка и уведомление с «Отменить» (возврат в работу). */
export function completeTaskWithUndo(taskId: string, deps: TaskMutationDeps) {
  return runTaskMutation(
    () => completeActivity(taskId),
    {
      success: TASK_ACTION_TEXT.completed,
      fallback: TASK_ACTION_TEXT.completeFailed,
      action: { label: TASK_ACTION_TEXT.undo, onClick: () => void reopenTask(taskId, deps) },
    },
    deps,
  );
}
